import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Avatar,
} from '@mui/material';
// @ts-ignore
import validator from 'validator';

import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import { getUser } from '@/lib/common';
import { AppDataGrid } from '@/components/ui';
import { Loader } from '@/components/loader/loader';
import { PermissionArea, UserDto } from '@cloudcost/shared-types/users';
import { GridColDef } from '@mui/x-data-grid';
import { hasUserAccessTo } from '@/lib/permissions';
import UserDialog from './user-dialog';
import { getPhotoUploadUrl } from '@/lib/azure-blob';
import { Pill } from '@/components/ds';
import { Tone } from '@/theme/tokens';

type PasswordLoginBadge = {
  label: string;
  tone: Tone;
};

function getPasswordLoginBadge(row: {
  local_login_enabled?: boolean;
  two_factor_enabled?: boolean;
}): PasswordLoginBadge {
  if (!row.local_login_enabled) {
    return { label: 'Disabled', tone: 'success' };
  }
  if (row.two_factor_enabled) {
    return { label: 'Enabled with 2FA', tone: 'success' };
  }
  return { label: 'Enabled without 2FA', tone: 'danger' };
}

function buildAdminPutBody(userValue: any, avatarUrl: string) {
  const body: Record<string, unknown> = {
    firstName: userValue.firstName,
    lastName: userValue.lastName,
    username: userValue.username,
    email: userValue.email,
    photo_path: avatarUrl,
    default_currency: userValue.default_currency,
    is_admin: userValue.is_admin ?? false,
    is_technical: userValue.is_technical ?? false,
    is_business: userValue.is_business ?? false,
    is_sponsor: userValue.is_sponsor ?? false,
  };
  if (userValue.password) {
    body.password = userValue.password;
  }
  return body;
}

export default function UsersManagementTable() {
  const [items, setItems] = useState<UserDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [blockingRolesDialog, setBlockingRolesDialog] = React.useState(false);
  const [blockingMessage, setBlockingMessage] = React.useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [alertFirstName, setAlertFirstName] = useState(false);
  const [alertLastName, setAlertLastName] = useState(false);
  const [alertUsername, setAlertUsername] = useState(false);
  const [alertEmail, setAlertEmail] = useState(false);
  const [alertPassword, setAlertPassword] = useState(false);
  const [alertDefaultCurrency, setAlertDefaultCurrency] = useState(false);

  const [open, setOpen] = React.useState(false);
  const [currenciesList, setCurrenciesList] = useState([]);
  const [userValue, setUserValue] = useState<any>([]);
  const [originalUsername, setOriginalUsername] = useState<string>('');
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [canEdit, setCanEdit] = useState(false);

  const isAdminUser = hasUserAccessTo(PermissionArea.ADMIN_PANEL);

  const PROFILE_TONES: Record<string, Tone> = {
    admin: 'warn',
    tech: 'primary',
    business: 'success',
    sponsor: 'ai',
  };

  const ROLE_TONES: Record<string, Tone> = {
    'cloud account owner': 'warn',
    'dept owner': 'primary',
    'system owner': 'success',
    'resource owner': 'ai',
    'budget owner': 'danger',
  };

  const columns: GridColDef[] = [
    {
      field: 'photo_path',
      width: 70,
      headerName: ' ',
      renderCell: (params: any) => {
        if (params.value) {
          return (
            <Avatar
              src={params.value}
              sx={{ width: 45, height: 45, mt: 1, mb: 1 }}
            />
          );
        }

        const initials =
          params.row.firstName?.charAt(0) + params.row.lastName?.charAt(0) ||
          params.row.username?.charAt(0);

        return (
          <Avatar
            src={params.value}
            sx={{ width: 45, height: 45, mt: 1, mb: 1 }}>
            {initials ? initials.toUpperCase() : ''}
          </Avatar>
        );
      },
    },
    {
      field: 'username',
      headerName: 'User name',
      flex: 0.45,
    },
    {
      field: 'firstName',
      headerName: 'First name',
      flex: 0.3,
    },
    {
      field: 'lastName',
      headerName: 'Last name',
      flex: 0.5,
    },
    {
      field: 'local_login_enabled',
      headerName: 'Password login',
      flex: 0.42,
      minWidth: 170,
      renderCell: (params: any) => {
        const badge = getPasswordLoginBadge(params.row);
        return <Pill tone={badge.tone}>{badge.label}</Pill>;
      },
    },
    {
      field: 'role',
      headerName: 'User profile',
      flex: 0.8,
      renderCell: (params: any) => {
        const profiles = [];

        if (params.row.is_admin) {
          profiles.push('admin');
        }
        if (params.row.is_technical) {
          profiles.push('tech');
        }
        if (params.row.is_business) {
          profiles.push('business');
        }
        if (params.row.is_sponsor) {
          profiles.push('sponsor');
        }

        return (
          <div
            style={{
              display: 'flex',
              gap: '5px',
              flexWrap: 'wrap',
              padding: '5px',
            }}>
            {profiles.map((role: string) => (
              <Pill key={role} tone={PROFILE_TONES[role] || 'neutral'}>
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </Pill>
            ))}
          </div>
        );
      },
    },
    {
      field: 'role2',
      headerName: 'User roles',
      flex: 0.8,
      renderCell: (params: any) => {
        const roles = [];

        if (params.row.cloud_account_owner) {
          roles.push('cloud account owner');
        }
        if (params.row.department_owner) {
          roles.push('dept owner');
        }
        if (params.row.system_owner) {
          roles.push('system owner');
        }
        if (params.row.resource_owner) {
          roles.push('resource owner');
        }

        return (
          <div
            style={{
              display: 'flex',
              gap: '5px',
              flexWrap: 'wrap',
              padding: '5px',
            }}>
            {roles.map((role: string) => (
              <Pill key={role} tone={ROLE_TONES[role] || 'neutral'}>
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </Pill>
            ))}
          </div>
        );
      },
    },
  ];

  const openBlockingRolesDialog = (message: string) => {
    setBlockingMessage(message);
    setBlockingRolesDialog(true);
  };

  const closeBlockingRolesDialog = () => {
    setBlockingRolesDialog(false);
    setBlockingMessage('');
  };

  const validateField = (field: string, value: any): boolean => {
    switch (field) {
      case 'firstName':
        const firstNameValid = !value || value.trim() === '';
        setAlertFirstName(firstNameValid);
        return !firstNameValid;
      case 'lastName':
        const lastNameValid = !value || value.trim() === '';
        setAlertLastName(lastNameValid);
        return !lastNameValid;
      case 'username':
        const usernameValid = !value || value.trim() === '' || value.length < 3;
        setAlertUsername(usernameValid);
        return !usernameValid;
      case 'email':
        const emailValid =
          !value || value.trim() === '' || !validator.isEmail(value);
        setAlertEmail(emailValid);
        return !emailValid;
      case 'password':
        if (userValue.new && (!value || value.trim() === '')) {
          setAlertPassword(true);
          return false;
        }
        if (value && value.trim() !== '') {
          const passwordValid = validator.isStrongPassword(value, {
            minLength: 6,
            minLowercase: 1,
            minUppercase: 1,
            minNumbers: 1,
          });
          setAlertPassword(!passwordValid);
          return passwordValid;
        }
        setAlertPassword(false);
        return true;
      case 'default_currency':
        const currencyValid = !value;
        setAlertDefaultCurrency(currencyValid);
        return !currencyValid;
      default:
        return true;
    }
  };

  const checkFieldValidity = (field: string, value: any): boolean => {
    switch (field) {
      case 'firstName':
        return value && value.trim() !== '';
      case 'lastName':
        return value && value.trim() !== '';
      case 'username':
        return value && value.trim() !== '' && value.length >= 3;
      case 'email':
        return value && value.trim() !== '' && validator.isEmail(value);
      case 'password':
        if (userValue.new && (!value || value.trim() === '')) {
          return false;
        }
        if (value && value.trim() !== '') {
          return validator.isStrongPassword(value, {
            minLength: 6,
            minLowercase: 1,
            minUppercase: 1,
            minNumbers: 1,
          });
        }
        return true;
      case 'default_currency':
        return !!value;
      default:
        return true;
    }
  };

  const validateAllFields = (): boolean => {
    const firstNameValid = validateField('firstName', userValue.firstName);
    const lastNameValid = validateField('lastName', userValue.lastName);
    const usernameValid = validateField('username', userValue.username);
    const emailValid = validateField('email', userValue.email);
    const passwordValid = validateField('password', userValue.password);
    const currencyValid = validateField(
      'default_currency',
      userValue.default_currency,
    );

    return (
      firstNameValid &&
      lastNameValid &&
      usernameValid &&
      emailValid &&
      passwordValid &&
      currencyValid
    );
  };

  const isFormValid = useMemo(() => {
    try {
      const firstNameValid = checkFieldValidity(
        'firstName',
        userValue.firstName,
      );
      const lastNameValid = checkFieldValidity('lastName', userValue.lastName);
      const usernameValid = checkFieldValidity('username', userValue.username);
      const emailValid = checkFieldValidity('email', userValue.email);
      const passwordValid = checkFieldValidity('password', userValue.password);
      const currencyValid = checkFieldValidity(
        'default_currency',
        userValue.default_currency,
      );

      return (
        firstNameValid &&
        lastNameValid &&
        usernameValid &&
        emailValid &&
        passwordValid &&
        currencyValid
      );
    } catch (error) {
      console.error('Error validating fields:', error);
      return false;
    }
  }, [userValue]);

  const validate = () => {
    return;
  };

  const handleOpen2 = () => {
    setUserValue({
      new: true,
      firstName: '',
      lastName: '',
      username: '',
      email: '',
      password: '',
      photo_path: '',
      is_admin: false,
      is_technical: false,
      is_business: false,
      is_sponsor: false,
      default_currency: currentUser?.default_currency || 'EUR',
    });
    setOriginalUsername('');
    setOpen(true);
    setCanEdit(true);

    setAlertFirstName(false);
    setAlertLastName(false);
    setAlertUsername(false);
    setAlertEmail(false);
    setAlertPassword(false);
    setAlertDefaultCurrency(false);
  };

  const handleOpen = (clickedUser: any) => {
    setOpen(true);

    setUserValue({ ...clickedUser.row, password: '' });
    setOriginalUsername(clickedUser.row.username);
    setCanEdit(Boolean(isAdminUser));

    setAlertFirstName(false);
    setAlertLastName(false);
    setAlertUsername(false);
    setAlertEmail(false);
    setAlertPassword(false);
    setAlertDefaultCurrency(false);
  };

  const canEditField = (fieldType: 'basic' | 'admin' | 'limited') => {
    if (!canEdit) return false;
    if (isAdminUser) return true;
    return fieldType === 'limited';
  };

  const handleClose = () => {
    setAlertFirstName(false);
    setAlertLastName(false);
    setAlertUsername(false);
    setAlertEmail(false);
    setAlertPassword(false);
    setAlertDefaultCurrency(false);
    setOpen(false);

    setUserValue({});
    setOriginalUsername('');
    setErrorMessage('');
  };

  const deleteUser = () => {
    setDialogLoading(true);
    fetchWrapper
      .post(urls.DevOps.deleteUser, { id: userValue.id })
      .then((data) => {
        console.log('Success!', data);
        handleClose();

        fetchWrapper
          .get(urls.DevOps.getUsersAll)
          .then((data) => setItems(data));
      })
      .catch((error) => {
        console.error('Error deleting user:', error);

        if (error.response && error.response.status === 400) {
          const errorData = error.response.data;
          if (errorData.blockingRoles && errorData.message) {
            openBlockingRolesDialog(errorData.message);
          } else {
            setErrorMessage(
              'Cannot delete user: ' + (errorData.error || 'Unknown error'),
            );
          }
        } else {
          setErrorMessage('An error occurred while deleting the user');
        }
      })
      .finally(() => {
        setDialogLoading(false);
      });
  };

  const createUser = async () => {
    if (!validateAllFields()) {
      return;
    }

    setDialogLoading(true);

    const existingUser = items.find(
      (user: any) =>
        user.username === userValue.username && user.id !== userValue.id,
    );

    if (existingUser && userValue.username) {
      setErrorMessage('Username already exists in the system.');
      setDialogLoading(false);
      return;
    }

    if (userValue.is_sponsor) {
      const existingSponsor = items.find(
        (user: any) => user.is_sponsor && user.id !== userValue.id,
      );
      if (existingSponsor) {
        setErrorMessage('Only one sponsor user can exist in the system.');
        setDialogLoading(false);
        return;
      }
    }

    if (validator.isEmail(userValue.email) !== true) {
      setDialogLoading(false);
      return;
    }

    try {
      const avatarUrl = userValue.photo_path?.startsWith('http')
        ? userValue.photo_path
        : await getPhotoUploadUrl(userValue.photo_path || '');

      if (userValue.new === true) {
        await fetchWrapper.post(urls.DevOps.createUser, {
          firstName: userValue.firstName,
          is_admin: userValue.is_admin ?? false,
          is_technical: userValue.is_technical ?? false,
          is_business: userValue.is_business ?? false,
          is_sponsor: userValue.is_sponsor ?? false,
          lastName: userValue.lastName,
          username: userValue.username,
          password: userValue.password,
          default_currency: userValue.default_currency,
          email: userValue.email,
        });

        const users = await fetchWrapper.get(urls.DevOps.getUsersAll);
        const created = users.find(
          (u: UserDto) => u.username === userValue.username,
        );
        if (!created?.id) {
          throw new Error('User was created but could not be loaded for update.');
        }

        await fetchWrapper.put(
          `${urls.DevOps.updateUserPassword}${created.id}`,
          buildAdminPutBody(userValue, avatarUrl),
        );
      } else {
        await fetchWrapper.put(
          `${urls.DevOps.updateUserPassword}${userValue.id}`,
          buildAdminPutBody(userValue, avatarUrl),
        );
      }

      const refreshed = await fetchWrapper.get(urls.DevOps.getUsersAll);
      setItems(refreshed);
      handleClose();
    } catch (error: any) {
      console.error('Error saving user:', error);
      setErrorMessage(error?.message || 'Failed to save user');
    } finally {
      setDialogLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdminUser) {
      return;
    }

    setLoading(true);

    Promise.all([
      fetchWrapper.get(urls.DevOps.getUsersAll),
      fetchWrapper.get(urls.DevOps.currencies),
    ])
      .then(([usersData, currenciesData]) => {
        setItems(usersData);
        setCurrenciesList(currenciesData);
      })
      .catch((error) => console.error('There was an error!', error))
      .finally(() => setLoading(false));

    setCurrentUser(getUser());
  }, [isAdminUser]);

  if (loading) {
    return <Loader />;
  }

  return (
    <div style={{ height: '100%' }}>
      <AppDataGrid
        style={{ height: '450px', marginLeft: '0px' }}
        rows={items}
        getRowHeight={() => 'auto'}
        loading={loading}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 5, page: 0 },
          },
        }}
        onCellClick={(clickedUser) => {
          handleOpen(clickedUser);
        }}
      />

      {hasUserAccessTo(PermissionArea.ADMIN_PANEL) && (
        <Button
          variant="contained"
          onClick={handleOpen2}
          color="primary"
          style={{ float: 'right', marginTop: '10px', marginBottom: '10px' }}>
          Add
        </Button>
      )}

      <UserDialog
        open={open}
        onClose={handleClose}
        userValue={userValue}
        setUserValue={setUserValue}
        currenciesList={currenciesList}
        items={items}
        canEditField={canEditField}
        onCreateUser={createUser}
        onDeleteUser={deleteUser}
        errorMessage={errorMessage}
        onClearError={() => setErrorMessage('')}
        alertFirstName={alertFirstName}
        alertLastName={alertLastName}
        alertUsername={alertUsername}
        alertEmail={alertEmail}
        alertPassword={alertPassword}
        alertDefaultCurrency={alertDefaultCurrency}
        validateField={validateField}
        validate={validate}
        currentUser={currentUser}
        hasAdminAccess={hasUserAccessTo(PermissionArea.ADMIN_PANEL)}
        loading={dialogLoading}
        isFormValid={isFormValid}
      />

      <Dialog
        open={blockingRolesDialog}
        onClose={closeBlockingRolesDialog}
        aria-labelledby="blocking-roles-dialog-title"
        aria-describedby="blocking-roles-dialog-description"
        maxWidth="sm"
        fullWidth>
        <DialogTitle id="blocking-roles-dialog-title">
          Cannot Delete User
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="blocking-roles-dialog-description"
            sx={{ whiteSpace: 'pre-line' }}>
            {blockingMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBlockingRolesDialog} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
